import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Badge, Flex, Text, Box, HStack, Select, Input,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import axiosInstance from '../../../api/AxiosInstance';
import FormularioEvento from './FormularioEvento';
import ModalEvidencias from './ModalEvidencias';

const ESTATUS_COLORS: Record<string, string> = {
  Activo: 'green',
  Cerrado: 'blue',
};

const TIPOS_EVENTO = ['Feria', 'Festival', 'Ceremonia', 'Exposición', 'Presentación', 'Otro'];

const TablaEventosCulturales: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const { isOpen: isOpenVer, onOpen: onOpenVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isOpenEditar, onOpen: onOpenEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();
  const { isOpen: isOpenEvidencias, onOpen: onOpenEvidencias, onClose: onCloseEvidencias } = useDisclosure();
  const { isOpen: isOpenEliminar, onOpen: onOpenEliminar, onClose: onCloseEliminar } = useDisclosure();
  const { isOpen: isOpenCerrar, onOpen: onOpenCerrar, onClose: onCloseCerrar } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [data, setData] = useState<any[]>([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setCargando(true);
    try {
      const res = await axiosInstance.get('/cultura/eventos');
      setData(res.data || []);
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const datosFiltrados = data.filter((item) => {
    const matchEstatus = filtroEstatus ? item.estatus === filtroEstatus : true;
    const matchTipo = filtroTipo ? item.tipoEvento === filtroTipo : true;
    const matchBusqueda = busqueda
      ? item.titulo?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.comunidad?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.responsable?.toLowerCase().includes(busqueda.toLowerCase())
      : true;
    return matchEstatus && matchTipo && matchBusqueda;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const handleVer = async (item: any) => {
    try {
      const res = await axiosInstance.get(`/cultura/eventos/${item.id}`);
      setSelectedItem(res.data);
    } catch {
      setSelectedItem(item);
    }
    onOpenVer();
  };

  const handleEditar = async (item: any) => {
    try {
      const res = await axiosInstance.get(`/cultura/eventos/${item.id}`);
      setSelectedItem(res.data);
    } catch {
      setSelectedItem(item);
    }
    onOpenEditar();
  };

  const handleEvidencias = async (item: any) => {
    try {
      const res = await axiosInstance.get(`/cultura/eventos/${item.id}`);
      setSelectedItem(res.data);
    } catch {
      setSelectedItem(item);
    }
    onOpenEvidencias();
  };

  const handleGuardado = () => {
    onCloseNuevo();
    onCloseEditar();
    fetchData();
  };

  const handleEvidenciasGuardadas = () => {
    onCloseEvidencias();
    fetchData();
  };

  const handleEliminarClick = (item: any) => { setSelectedItem(item); onOpenEliminar(); };
  const handleCerrarClick = (item: any) => { setSelectedItem(item); onOpenCerrar(); };

  const handleEliminarConfirmar = async () => {
    if (!selectedItem) return;
    setProcesando(true);
    try {
      await axiosInstance.delete(`/cultura/eventos/${selectedItem.id}`);
      toast({ title: 'Evento eliminado', status: 'success', duration: 3000 });
      onCloseEliminar();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo eliminar.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleCerrarConfirmar = async () => {
    if (!selectedItem) return;
    setProcesando(true);
    try {
      await axiosInstance.put(`/cultura/eventos/${selectedItem.id}/cerrar`);
      toast({ title: 'Evento cerrado', status: 'success', duration: 3000 });
      onCloseCerrar();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo cerrar.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleExportExcel = () => {
    const filas = datosFiltrados.map((item) => ({
      'Título': item.titulo,
      'Tipo': item.tipoEvento,
      'Comunidad': item.comunidad,
      'Responsable': item.responsable,
      'Fecha del Evento': item.fechaEvento ? new Date(item.fechaEvento).toLocaleDateString('es-MX') : '',
      'Fecha Registro': item.fechaRegistro ? new Date(item.fechaRegistro).toLocaleDateString('es-MX') : '',
      'Estatus': item.estatus,
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Eventos Culturales');
    XLSX.writeFile(wb, `Eventos_Culturales_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const fmt = (v: string) =>
    v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  return (
    <Box>
      {/* Barra de herramientas */}
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input
            placeholder="Buscar por título, comunidad o responsable..."
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }}
            size="sm"
            w={{ base: 'full', md: '250px' }}
          />
          <Select
            placeholder="Filtrar por tipo"
            value={filtroTipo}
            onChange={(e) => { setFiltroTipo(e.target.value); setCurrentPage(1); }}
            size="sm"
            w="160px"
          >
            {TIPOS_EVENTO.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
          <Select
            placeholder="Filtrar por estatus"
            value={filtroEstatus}
            onChange={(e) => { setFiltroEstatus(e.target.value); setCurrentPage(1); }}
            size="sm"
            w="150px"
          >
            <option value="Activo">Activo</option>
            <option value="Cerrado">Cerrado</option>
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>
            Excel
          </Button>
          <Button
            size="sm"
            leftIcon={<AddIcon />}
            bg="#5b182b"
            color="white"
            _hover={{ bg: '#691f30' }}
            onClick={onOpenNuevo}
          >
            Nuevo evento
          </Button>
        </HStack>
      </Flex>

      {/* Tabla */}
      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg="#5b182b">
            <Tr>
              <Th color="white" fontSize="xs">Título</Th>
              <Th color="white" fontSize="xs">Tipo</Th>
              <Th color="white" fontSize="xs">Comunidad</Th>
              <Th color="white" fontSize="xs">Fecha Evento</Th>
              <Th color="white" fontSize="xs">Fecha Registro</Th>
              <Th color="white" fontSize="xs">Responsable</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={8} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={8} textAlign="center" py={8}><Text color="gray.400">No se encontraron eventos</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td maxW="200px">
                    <Text noOfLines={1} title={item.titulo} fontSize="sm">{item.titulo}</Text>
                    <Badge fontSize="2xs" colorScheme={item.tipoRegistro === 'PASADO' ? 'purple' : 'cyan'} ml={0}>
                      {item.tipoRegistro}
                    </Badge>
                  </Td>
                  <Td>{item.tipoEvento}</Td>
                  <Td>{item.comunidad}</Td>
                  <Td>{fmt(item.fechaEvento)}</Td>
                  <Td>{fmt(item.fechaRegistro)}</Td>
                  <Td>{item.responsable}</Td>
                  <Td>
                    <Badge colorScheme={ESTATUS_COLORS[item.estatus] || 'gray'} fontSize="xs" px={2} py={0.5} borderRadius="full">
                      {item.estatus}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1} flexWrap="wrap">
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => handleVer(item)}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => handleEditar(item)}>Editar</Button>
                      <Button size="xs" colorScheme="teal" variant="outline" onClick={() => handleEvidencias(item)}>Evidencias</Button>
                      {item.estatus === 'Activo' && (
                        <Button size="xs" colorScheme="blue" variant="solid" onClick={() => handleCerrarClick(item)}>Cerrar</Button>
                      )}
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleEliminarClick(item)}>Eliminar</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Paginación */}
      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">
            Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}
          </Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button key={page} size="xs" bg={currentPage === page ? '#5b182b' : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>
                  {page}
                </Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      {/* Modal: Ver */}
      <Modal isOpen={isOpenVer} onClose={onCloseVer} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Detalle del Evento Cultural
          </ModalHeader>
          <ModalBody py={5}>
            {selectedItem && <FormularioEvento modo="view" data={selectedItem} onCerrarEvento={() => { onCloseVer(); handleCerrarClick(selectedItem); }} />}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button onClick={onCloseVer}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal: Editar */}
      <Modal isOpen={isOpenEditar} onClose={onCloseEditar} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Editar Evento Cultural
          </ModalHeader>
          <ModalBody py={5}>
            {selectedItem && (
              <FormularioEvento modo="edit" data={selectedItem} onGuardado={handleGuardado} onCancelar={onCloseEditar} />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal: Nuevo */}
      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Nuevo Evento Cultural
          </ModalHeader>
          <ModalBody py={5}>
            <FormularioEvento modo="new" onGuardado={handleGuardado} onCancelar={onCloseNuevo} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal: Evidencias */}
      {selectedItem && (
        <ModalEvidencias
          isOpen={isOpenEvidencias}
          onClose={onCloseEvidencias}
          evento={selectedItem}
          onGuardado={handleEvidenciasGuardadas}
        />
      )}

      {/* AlertDialog: Cerrar evento */}
      <AlertDialog isOpen={isOpenCerrar} leastDestructiveRef={cancelRef} onClose={onCloseCerrar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="#5b182b">Cerrar evento</AlertDialogHeader>
            <AlertDialogBody>
              ¿Confirmas el cierre del evento <strong>{selectedItem?.titulo}</strong>? El estatus cambiará a <strong>Cerrado</strong>.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseCerrar} isDisabled={procesando}>Cancelar</Button>
              <Button colorScheme="blue" onClick={handleCerrarConfirmar} ml={3} isLoading={procesando} loadingText="Cerrando...">
                Confirmar cierre
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* AlertDialog: Eliminar */}
      <AlertDialog isOpen={isOpenEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="#5b182b">Eliminar evento</AlertDialogHeader>
            <AlertDialogBody>
              ¿Eliminar el evento <strong>{selectedItem?.titulo}</strong>? Se eliminarán todos los archivos asociados. Esta acción no se puede deshacer.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={procesando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleEliminarConfirmar} ml={3} isLoading={procesando} loadingText="Eliminando...">
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaEventosCulturales;
